import React from 'react';
import {
  AirflowCalibrationState,
  AirflowRangeStatus,
  SystemAirflowCalibration,
  SystemAirflowCleanWindow,
} from '../types';

/**
 * FS-747 — how far one airflow value can be trusted, as a compact badge that
 * sits directly beside the CFM, with the full explanation in its title.
 *
 * The wording is FS-648's, moved here from the airflow chart and reworded for
 * ONE value per filter period (Plan 127): there is no trend any more, only a
 * number that is calibrated, partly calibrated, or approximate for a stated
 * reason. The server decides the state and the reason from the device's
 * current match lineage; nothing here infers them. An approximate value is
 * shown and labeled, never hidden (FS-732).
 */

export type CalibrationTone = 'green' | 'amber' | 'gray';

export interface CalibrationWords {
  label: string;
  title: string;
  tone: CalibrationTone;
}

const TONE_CLASS: Record<CalibrationTone, string> = {
  green: 'border border-status-success-border bg-status-success text-status-success-foreground',
  amber: 'border border-status-warning-border bg-status-warning text-status-warning-foreground',
  gray: 'border border-status-neutral-border bg-status-neutral text-status-neutral-foreground',
};

const CERTIFIED_SOURCES = new Set(['maedb', 'filter_label', 'certified_scaled']);

/** The label for a curve that is not certified, by its source tier. */
const uncertifiedLabel = (source: string | null): string =>
  source === 'extrapolated'
    ? 'Extrapolated'
    : source === 'manual'
      ? 'Manual entry'
      : source === 'manufacturer'
        ? 'Manufacturer'
        : 'Estimated';

const uncertifiedBody = (source: string | null, donor: string | null): string =>
  source === 'extrapolated'
    ? `No certified curve for this exact filter; airflow is area-scaled from a same-brand/MERV sibling${donor ? ` (donor: ${donor})` : ''}. Within about 5% on the validation point.`
    : source === 'manual'
      ? 'Curve was hand-entered by an operator from a datasheet or label; not certified. Treat this CFM as approximate.'
      : source === 'manufacturer'
        ? 'Manufacturer-published curve; not certified. Treat this CFM as approximate.'
        : 'Estimated from a similar filter; not certified. Treat this CFM as approximate.';

/**
 * FS-648: why a certified-source match did not calibrate this value, from
 * the reason the server read off the stored lineage.
 */
const certifiedApproximate = (
  c: SystemAirflowCalibration,
): Pick<CalibrationWords, 'label' | 'title'> => {
  const scaled = c.source === 'certified_scaled';
  switch (c.reason) {
    case 'size_not_verified':
      return {
        label: 'Size not verified',
        title:
          `Approximate: size not verified. Certified curve${c.source ? ` (${c.source})` : ''}, but the ` +
          "filter's size was not verified against it; the matched curve may be for a different size. " +
          'Treat this CFM as approximate.',
      };
    case 'product_not_resolved':
      return {
        label: 'Product not resolved',
        title: scaled
          ? 'Approximate: product not resolved. Certified curve for this product line, scaled to this ' +
            'size, but the filter record does not say which of the listings sharing this brand, size and ' +
            'rating it is, or names a model the catalog does not list. This CFM is approximate.'
          : 'Approximate: product not resolved. Certified curve, size verified, but several listings ' +
            'share this size and rating and the filter record does not say which; the curve used is the ' +
            'middle one. This CFM is approximate.',
      };
    case 'no_calibrated_day':
      return {
        label: 'No calibrated day',
        title:
          "Approximate: no calibrated day. This filter's certified curve is confirmed for this device, " +
          'but none of the clean days behind this value was computed on it: they predate the match, or ' +
          'were computed on an earlier curve. This CFM is approximate.',
      };
    default:
      return {
        label: 'Not calibrated',
        title:
          'Approximate: not calibrated. The certified curve did not calibrate this value and the ' +
          'response does not say why: the size may not be verified, the product may not be resolved, ' +
          'or no clean day was computed on it. This CFM is approximate.',
      };
  }
};

/**
 * The badge words for a value, or null when there is nothing to say (no
 * value: state null).
 * `cfmDays` is how many clean days had a CFM, for "n of m days".
 */
export const calibrationWords = (
  c: SystemAirflowCalibration,
  cfmDays?: number,
): CalibrationWords | null => {
  if (c.state === 'calibrated') {
    // FS-686: a certified Title 20 curve filed at another size of the same
    // product, scaled by face area as Title 20 allows for an untested size.
    // It calibrates; the label still says it was scaled.
    if (c.source === 'certified_scaled' && c.scaled_from) {
      const label = `Calibrated (scaled from ${c.scaled_from})`;
      return {
        label,
        tone: 'green',
        title:
          `${label}. Uses a certified Title 20 curve for the same product at ${c.scaled_from}, scaled ` +
          'to this size by face area as Title 20 itself allows. This CFM is reliable.',
      };
    }
    return {
      label: 'Calibrated',
      tone: 'green',
      title: `Calibrated. Uses a certified Title 20 curve${c.source ? ` (${c.source})` : ''}. This CFM is reliable.`,
    };
  }
  if (c.state === 'partly') {
    const of = typeof cfmDays === 'number' && cfmDays > 0 ? ` of ${cfmDays}` : '';
    const days = c.calibrated_days === 1 && !of ? 'day' : 'days';
    return {
      label: `Partly calibrated (${c.calibrated_days}${of} ${days})`,
      tone: 'amber',
      title:
        `Partly calibrated: ${c.calibrated_days}${of} clean days behind this value were computed on a ` +
        "certified Title 20 curve with the filter's size and product confirmed; the others were not, " +
        'so this CFM is partly approximate.',
    };
  }
  if (c.state !== 'approximate') return null;
  if (c.reason === 'no_match') {
    // The stored clean days had a CFM, but the device has no current match
    // (the filter changed or was cleared since): the number stands, labeled.
    return {
      label: 'Approximate — no current filter match',
      tone: 'amber',
      title:
        'These days were computed on a filter match the device no longer has; the value is ' +
        'approximate until the filter is matched again.',
    };
  }
  if (c.reason === 'not_certified') {
    const label = uncertifiedLabel(c.source);
    return {
      label,
      tone: 'amber',
      title: `Approximate: ${label.toLowerCase()}. ${uncertifiedBody(c.source, c.donor)}`,
    };
  }
  if (c.reason === null) {
    return { label: 'Approximate', tone: 'amber', title: 'Approximate. This CFM is not calibrated.' };
  }
  return { ...certifiedApproximate(c), tone: 'amber' };
};

/**
 * The curve a Clip's filter matched, named when there is no value to label
 * (status no_clean_airflow). It never says "Calibrated": nothing was.
 */
export const curveWords = (c: SystemAirflowCalibration): CalibrationWords | null => {
  if (!c.source) return null;
  if (CERTIFIED_SOURCES.has(c.source)) {
    const scaled = c.source === 'certified_scaled' && c.scaled_from ? ` scaled from ${c.scaled_from}` : '';
    return {
      label: `Certified curve${scaled}`,
      tone: 'gray',
      title: `The filter matched a certified Title 20 curve (${c.source})${scaled}. No clean day had a CFM on it.`,
    };
  }
  const label = uncertifiedLabel(c.source);
  return {
    label,
    tone: 'gray',
    title: `The filter matched a curve that is not certified (${label.toLowerCase()}). ${uncertifiedBody(c.source, c.donor)}`,
  };
};

/** Led by a space, so the CFM and its label never read as one word when copied. */
const Badge: React.FC<{ words: CalibrationWords; testId: string }> = ({ words, testId }) => (
  <>
    {' '}
    <span
      className={`ml-1 whitespace-nowrap rounded-sm px-1.5 py-0.5 align-middle text-xs font-medium ${TONE_CLASS[words.tone]}`}
      title={words.title}
      data-testid={testId}
    >
      {words.label}
    </span>
  </>
);

/** One Clip's value: its calibration badge, or nothing when there is nothing to say. */
export const AirflowCalibration: React.FC<{
  calibration: SystemAirflowCalibration | null | undefined;
  /** Clean days with a CFM (the response's clean_cfm_days). */
  cfmDays?: number;
}> = ({ calibration, cfmDays }) => {
  if (!calibration) return null;
  const words = calibrationWords(calibration, cfmDays);
  return words ? <Badge words={words} testId="airflow-calibration" /> : null;
};

/** The curve a Clip with no clean airflow matched, when the response names one. */
export const AirflowCurve: React.FC<{ calibration: SystemAirflowCalibration | null | undefined }> = ({
  calibration,
}) => {
  if (!calibration) return null;
  const words = curveWords(calibration);
  return words ? <Badge words={words} testId="airflow-curve" /> : null;
};

const SYSTEM_WORDS: Record<AirflowCalibrationState, CalibrationWords> = {
  calibrated: {
    label: 'Calibrated',
    tone: 'green',
    title: "Every Clip's value in this figure is calibrated on a certified Title 20 curve.",
  },
  partly: {
    label: 'Partly calibrated',
    tone: 'amber',
    title: "Some of the Clips' values in this figure are calibrated and some are approximate; see each Clip.",
  },
  approximate: {
    label: 'Approximate',
    tone: 'amber',
    title: "None of the Clips' values in this figure is calibrated; see each Clip for why.",
  },
};

/** The system figure's calibration, over the Clips that went into it. */
export const SystemAirflowCalibrationBadge: React.FC<{
  state: AirflowCalibrationState | null | undefined;
}> = ({ state }) => (state ? <Badge words={SYSTEM_WORDS[state]} testId="system-airflow-calibration" /> : null);

/**
 * Plan 127 Phase 3, FS-878 — where the pressure behind a value sat on the
 * filter's published curve, said only when it was not inside it. Every place
 * that shows an airflow value (or a figure made from one) reads these words,
 * so one value is never worded two ways:
 * - `bound` goes before the number: a clamped_low value reads "under 472
 *   CFM", a clamped_high one "over 1,850 CFM". The CFM is the curve's end
 *   point, not a measurement.
 * - `label` is the short form beside a value where space is tight (a table
 *   row); `title` is the full line, shown under the main value and as the
 *   short form's hover text.
 * Null for in_range, null, or an older response without the field.
 */
export type AirflowBound = 'under' | 'over';

export interface RangeWords {
  /** The word before a curve-limited number; null when the number is not limited. */
  bound: AirflowBound | null;
  label: string;
  title: string;
}

export const CURVE_LOW_LINE =
  "Pressure is below this filter's published curve, so this is the curve's lowest point, not a measurement.";
export const CURVE_HIGH_LINE =
  "Pressure is above this filter's published curve, so this is the curve's highest point, not a measurement.";

export const rangeWords = (status: AirflowRangeStatus | null | undefined): RangeWords | null => {
  switch (status) {
    case 'clamped_low':
      return { bound: 'under', label: 'below the published curve', title: CURVE_LOW_LINE };
    case 'clamped_high':
      return { bound: 'over', label: 'above the published curve', title: CURVE_HIGH_LINE };
    case 'power_law':
      return { bound: null, label: 'single-point curve', title: 'Estimated from a single-point curve.' };
    default:
      return null;
  }
};

/**
 * FS-878 — the bound of a sum of values: under when every curve-limited value
 * in it is under, over when every one is over, mixed when there are both (the
 * real total could be either side), null when none is limited.
 */
export const combinedBound = (
  statuses: Array<AirflowRangeStatus | null | undefined>,
): AirflowBound | 'mixed' | null => {
  const bounds = new Set(statuses.map((s) => rangeWords(s)?.bound).filter(Boolean));
  if (bounds.size === 0) return null;
  if (bounds.size > 1) return 'mixed';
  return bounds.has('under') ? 'under' : 'over';
};

/** FS-878 — the short form beside a total that includes a value outside its curve. */
export const totalRangeLabel = (statuses: Array<AirflowRangeStatus | null | undefined>): string | null => {
  const words = statuses.map(rangeWords).filter((w): w is RangeWords => w !== null);
  if (words.length === 0) return null;
  return words.some((w) => w.bound)
    ? "includes a value at the end of its filter's published curve"
    : 'includes a value estimated from a single-point curve';
};

/**
 * FS-878 — the word before a CFM per ton, and the sentence its title adds.
 * Divided by an "up to" tonnage (a maximum) the figure is a minimum; made
 * from a curve-limited airflow it is limited the same way. When the two push
 * opposite ways nothing can be said about the direction, and the title says so.
 */
export const perTonBoundWords = (
  airflow: AirflowBound | 'mixed' | null | undefined,
  tonnageUpperBound: boolean,
): { word: string | null; note: string } => {
  const upTo = 'the tonnage is an "up to" figure';
  if (airflow === 'mixed') {
    return {
      word: null,
      note:
        " Some of the airflow is at an end of its filter's published curve, so the real CFM per ton " +
        'could be higher or lower.',
    };
  }
  if (airflow === 'under') {
    return tonnageUpperBound
      ? {
          word: null,
          note:
            " The airflow is the lowest point of the filter's published curve and " +
            `${upTo}, so the real CFM per ton could be higher or lower.`,
        }
      : {
          word: 'under',
          note: " The airflow is at the lowest point of a filter's published curve, so the real CFM per ton is lower.",
        };
  }
  if (airflow === 'over') {
    return {
      word: 'over',
      note:
        " The airflow is at the highest point of a filter's published curve" +
        (tonnageUpperBound ? ` and ${upTo}` : '') +
        ', so the real CFM per ton is higher.',
    };
  }
  return tonnageUpperBound
    ? { word: 'at least', note: ` The tonnage is an "up to" figure, so the real CFM per ton is this or higher.` }
    : { word: null, note: '' };
};

/**
 * "under" / "over" before a curve-limited number, smaller than the number and
 * led into it by a space, so the text reads "under 472 CFM" when copied.
 * Nothing for an unlimited number (or a mixed sum, which the note beside it explains).
 */
export const BoundWord: React.FC<{ bound: string | null | undefined }> = ({ bound }) =>
  bound && bound !== 'mixed' ? (
    <>
      <span className="font-sans text-sm font-normal text-amber-700" data-testid="airflow-bound">
        {bound}
      </span>{' '}
    </>
  ) : null;

/**
 * Led by a space, like the badge, so it never runs into the CFM when copied.
 * `full`: under a main value, a curve-limited value gets its whole line on a
 * line of its own; the short form stays for a single-point curve and for
 * tight places (table rows), with the line as its title.
 */
export const AirflowRangeNote: React.FC<{ status: AirflowRangeStatus | null | undefined; full?: boolean }> = ({
  status,
  full = false,
}) => {
  const words = rangeWords(status);
  if (!words) return null;
  if (full && words.bound) {
    return (
      <>
        {' '}
        <span
          className="mt-0.5 block text-xs text-amber-700"
          title={
            "Airflow is read off the filter's pressure-drop curve. This pressure is " +
            (words.bound === 'under'
              ? "lower than the curve's first point, so the real airflow is lower than shown."
              : "higher than the curve's last point, so the real airflow is higher than shown.")
          }
          data-testid="airflow-range-line"
        >
          {words.title}
        </span>
      </>
    );
  }
  return (
    <>
      {' '}
      <span
        className="ml-1 whitespace-nowrap align-middle text-xs text-amber-700"
        title={words.title}
        data-testid="airflow-range"
      >
        {words.label}
      </span>
    </>
  );
};

/**
 * FS-878 — a value made from fewer than three clean days is shown (rather
 * than nothing) and marked: "Based on 2 days". Null at three days, or when
 * there is no value.
 */
export const shortEvidenceWords = (days: number | null | undefined): CalibrationWords | null => {
  if (typeof days !== 'number' || days < 1 || days >= 3) return null;
  const n = days === 1 ? '1 day' : `${days} days`;
  return {
    label: `Based on ${n}`,
    tone: 'amber',
    title:
      `Only ${days === 1 ? '1 clean day' : `${days} clean days`} went into this value. It is normally ` +
      'the average of the first 3, so treat it as less certain.',
  };
};

export const ShortEvidenceBadge: React.FC<{ days: number | null | undefined }> = ({ days }) => {
  const words = shortEvidenceWords(days);
  return words ? <Badge words={words} testId="airflow-short-evidence" /> : null;
};

/** FS-878 — a plain badge beside a figure, in the calibration badge's style. */
export const NoteBadge: React.FC<{ label: string; title: string; testId: string; tone?: CalibrationTone }> = ({
  label,
  title,
  testId,
  tone = 'gray',
}) => <Badge words={{ label, title, tone }} testId={testId} />;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Sep 11, 12, 13"; the month is repeated only when it changes
 * ("Sep 30, Oct 1, 2"). Read from the YYYY-MM-DD text, never through Date,
 * so no timezone moves a day.
 */
export const shortDates = (dates: string[]): string => {
  let lastMonth = '';
  return dates
    .map((d) => {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
      if (!m) return d;
      const month = MONTHS[Number(m[2]) - 1] ?? m[2];
      const day = String(Number(m[3]));
      const key = `${m[1]}-${m[2]}`;
      const text = key === lastMonth ? day : `${month} ${day}`;
      lastMonth = key;
      return text;
    })
    .join(', ');
};

/**
 * FS-747 — which clean days the value came from, and where the window
 * stands: "from Sep 11, 12, 13"; "from Sep 11 (1 of 3 days)" when it closed
 * short; "from Sep 11, collecting (1 of 3)" while it is open; a closed
 * window with no running day says so. When some
 * clean days had no CFM, says how many did. Null when there is nothing to
 * say (no window).
 */
export const cleanDaysText = (
  window: SystemAirflowCleanWindow | null | undefined,
  cfmDays?: number,
): string | null => {
  if (!window || window.state === null) return null;
  const parts: string[] = [];
  const dates = Array.isArray(window.dates) ? window.dates : [];
  if (dates.length > 0) parts.push(`from ${shortDates(dates)}`);
  if (window.state === 'open') {
    parts.push(`collecting (${window.running_days} of 3)`);
  }
  let text = parts.join(', ');
  if (window.state === 'closed' && window.running_days > 0 && window.running_days < 3) {
    text += ` (${window.running_days} of 3 days)`;
  }
  if (window.state === 'closed' && window.running_days === 0) {
    text = "no running day in this Clip's 14-day window for this filter period";
  }
  if (typeof cfmDays === 'number' && cfmDays < dates.length && cfmDays > 0) {
    text += `; CFM on ${cfmDays} of them`;
  }
  return text || null;
};

/**
 * FS-747 / FS-848 — the Clip's first day with the blower running came some
 * days into the filter period (counted from the period start, the day after
 * the change), so its first running days may not be a clean filter's. Said beside the clean
 * days (short form, full sentence in the title); the calibration badge does
 * not change. Nothing when the lag is 0 or the response does not carry it.
 */
export const ReportingLagNote: React.FC<{ window: SystemAirflowCleanWindow | null | undefined }> = ({
  window,
}) => {
  const lag = window?.reporting_lag_days;
  if (typeof lag !== 'number' || !(lag > 0)) return null;
  const days = `${lag} ${lag === 1 ? 'day' : 'days'}`;
  return (
    <span
      className="block text-xs text-amber-700"
      data-testid="reporting-lag"
      title={`The Clip first recorded the blower running ${days} into this filter period; the filter may not have been clean.`}
    >
      blower first ran {days} into this filter
    </span>
  );
};
